import { Paper, Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { AutostoreBin } from "~/features/autostoreBin";
import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";
import { selectWorkstationAutostoreGridId } from "~/redux/selectors/workstationsSelectors";
import { useEmptyBinReportQuery } from "~/redux/warehouse/autostoreGrid.hooks";
import { useGetBinConfigurationsQuery } from "~/redux/warehouse/bin.hooks";

import {
  setSourceBinConfigurationId,
  setTargetBinConfigurationId
} from "./binReconfiguration.slice";

export const BinConfiguration = ({
  binConfigurationId,
  variant
}: {
  binConfigurationId: Guid;
  variant: "source" | "target";
}) => {
  const dispatch = useAppDispatch();
  const sourceBinConfigurationId = useAppSelector(
    (store) => store.binReconfiguration.sourceBinConfigurationId
  );
  const targetBinConfigurationId = useAppSelector(
    (store) => store.binReconfiguration.targetBinConfigurationId
  );
  const siteAutostoreGridId = useAppSelector(selectWorkstationAutostoreGridId);
  const trackedPageName = useMixpanelPageName();

  const { emptyCompartmentReport } = useEmptyBinReportQuery(
    siteAutostoreGridId ?? skipToken,
    {
      selectFromResult: ({ data }) => {
        return {
          emptyCompartmentReport: data?.find(
            (r) => r.binConfigurationId === binConfigurationId
          )
        };
      }
    }
  );

  const { data: allBinConfigurations, binConfiguration } =
    useGetBinConfigurationsQuery(undefined, {
      selectFromResult: ({ data }) => ({
        data,
        binConfiguration: data?.find(
          (bc) => bc.configurationId === binConfigurationId
        )
      })
    });

  const variantBinConfigurationId =
    variant === "source" ? sourceBinConfigurationId : targetBinConfigurationId;

  const oppositeVariantBinConfigurationId =
    variant === "source" ? targetBinConfigurationId : sourceBinConfigurationId;
  const oppositeVariantBinConfiguration = allBinConfigurations?.find(
    (bc) => bc.configurationId === oppositeVariantBinConfigurationId
  );

  const isSameTypeAsOpposite =
    oppositeVariantBinConfiguration &&
    binConfiguration &&
    oppositeVariantBinConfiguration.verticalCompartmentCount ===
      binConfiguration.verticalCompartmentCount &&
    oppositeVariantBinConfiguration.horizontalCompartmentCount ===
      binConfiguration.horizontalCompartmentCount;

  const isDisabled = variant === "target" && isSameTypeAsOpposite;
  const isSelected =
    variantBinConfigurationId === binConfigurationId && !isDisabled;

  const handleSelectBinConfiguration = () => {
    if (variant === "source") {
      dispatch(setSourceBinConfigurationId(binConfigurationId));
      mixpanelTrack({
        trackedPageName,
        label: "Select Source Bin Configuration",
        type: "Button Click",
        eventProperties: {
          "Bin Configuration Id": binConfigurationId
        }
      });

      if (isSameTypeAsOpposite) {
        dispatch(setTargetBinConfigurationId(""));
      }
    } else if (!isDisabled) {
      mixpanelTrack({
        trackedPageName,
        label: "Select Target Bin Configuration",
        type: "Button Click",
        eventProperties: {
          "Bin Configuration Id": binConfigurationId
        }
      });
      dispatch(setTargetBinConfigurationId(binConfigurationId));
    }
  };

  return (
    <>
      {variant === "source" && emptyCompartmentReport && (
        <Typography variant="h4" gridRow={2} justifySelf="center">
          {emptyCompartmentReport?.binsWithNoInventory} Empty Bins
        </Typography>
      )}
      <Paper
        role="button"
        aria-label={`${variant}-${emptyCompartmentReport?.numberOfCompartments}-compartment-bin`}
        onClick={handleSelectBinConfiguration}
        sx={{
          border: "8px solid",
          borderColor: isSelected ? "primary.main" : "transparent",
          backgroundColor: isSelected
            ? "background.lightBlue"
            : "background.gray",
          p: 2,
          opacity: isDisabled ? 0.5 : 1
        }}
      >
        <AutostoreBin
          state={"Port Closed"}
          numberOfColumns={binConfiguration?.verticalCompartmentCount || 1}
          numberOfRows={binConfiguration?.horizontalCompartmentCount || 1}
          pickQuantity=""
          pickCompartment={null}
        />
      </Paper>
    </>
  );
};
